import { useCallback, useEffect, useRef } from 'react';

interface ScrollOptions {
  threshold?: number; // Distance from bottom in px to consider "at bottom"
}

interface UseMobileChatScrollReturn {
  containerRef: React.RefObject<HTMLDivElement>;
  autoScrollEnabled: boolean;
  setAutoScrollEnabled: (enabled: boolean) => void;
  scrollToBottom: (smooth?: boolean) => void;
  handleScroll: () => void;
}

/**
 * Hook for managing scroll behavior in the mobile chat.
 * Handles auto-scroll to new messages and detects manual scroll to disable auto-scroll.
 */
export function useMobileChatScroll(
  isLoading: boolean,
  streamingContent: string,
  options: ScrollOptions = {}
): UseMobileChatScrollReturn {
  const { threshold = 100 } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);

  // Scroll to bottom function
  const scrollToBottom = useCallback((smooth = true) => {
    if (containerRef.current && autoScrollEnabled) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  }, [autoScrollEnabled]);

  // Check if user has scrolled up (away from bottom)
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    // Disable auto-scroll if user has scrolled more than threshold from bottom
    if (distanceFromBottom > threshold && autoScrollEnabled) {
      setAutoScrollEnabled(false);
    }

    // Re-enable auto-scroll if user scrolls near bottom
    if (distanceFromBottom <= threshold && !autoScrollEnabled) {
      setAutoScrollEnabled(true);
    }
  }, [autoScrollEnabled, threshold]);

  // Auto-scroll to bottom when new content arrives
  useEffect(() => {
    // Only scroll if auto-scroll is enabled
    if (!autoScrollEnabled) return;

    // Small delay to allow DOM to update
    const timer = setTimeout(() => {
      scrollToBottom(true);
    }, 50);

    return () => clearTimeout(timer);
  }, [isLoading, streamingContent, autoScrollEnabled, scrollToBottom]);

  // Re-enable auto-scroll when new messages arrive
  useEffect(() => {
    if (isLoading) {
      setAutoScrollEnabled(true);
    }
  }, [isLoading]);

  return {
    containerRef,
    autoScrollEnabled,
    setAutoScrollEnabled,
    scrollToBottom,
    handleScroll,
  };
}

import { useState } from 'react';
